import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { useTheme } from '@mui/material/styles';
import _ from 'lodash';

const resolveRowKey = (row, rowKey) => {
  if (_.isFunction(rowKey)) return rowKey(row);
  return row?.[rowKey];
};

const ResponsiveDataTable = ({
  columns = [],
  rows = [],
  rowKey = 'id',
  emptyMessage = 'No hay registros para mostrar.',
  mobileTitle = null,
  mobileSubtitle = null,
  renderRowActions = null,
  tableSize = 'small',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedRows, setExpandedRows] = useState({});

  const visibleColumns = useMemo(() => _.filter(columns, (column) => column?.key !== 'actions'), [columns]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderValue = (column, row) => {
    if (_.isFunction(column?.render)) return column.render(row);
    return row?.[column?.key];
  };

  if (!rows?.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 1 }}>
        {emptyMessage}
      </Typography>
    );
  }

  if (!isMobile) {
    return (
      <Table size={tableSize}>
        <TableHead>
          <TableRow>
            {_.map(columns, (column) => (
              <TableCell key={`head-${column.key}`} align={column.align || 'left'}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {_.map(rows, (row) => (
            <TableRow key={resolveRowKey(row, rowKey)}>
              {_.map(columns, (column) => (
                <TableCell key={`row-${resolveRowKey(row, rowKey)}-${column.key}`} align={column.align || 'left'}>
                  {renderValue(column, row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Stack spacing={1.25}>
      {_.map(rows, (row) => {
        const id = resolveRowKey(row, rowKey);
        const expanded = !!expandedRows[id];

        return (
          <Card variant="outlined" key={`mobile-row-${id}`}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  {_.isFunction(mobileTitle) ? (
                    <Typography variant="h6" sx={{ mb: 0.25 }}>{mobileTitle(row)}</Typography>
                  ) : (
                    <Typography variant="h6" sx={{ mb: 0.25 }}>{renderValue(visibleColumns[0], row)}</Typography>
                  )}
                  {_.isFunction(mobileSubtitle) && (
                    <Typography variant="body2" color="text.secondary">
                      {mobileSubtitle(row)}
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={0.25} alignItems="center">
                  {_.isFunction(renderRowActions) && renderRowActions(row, true)}
                  <IconButton
                    size="small"
                    onClick={() => toggleRow(id)}
                    sx={{
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s ease',
                    }}>
                    <ExpandMoreOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              <Collapse in={expanded}>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {_.map(visibleColumns, (column, index) => {
                    if (column.mobileHidden) return null;
                    const value = renderValue(column, row);
                    const normalizedValue = _.isNil(value) || value === '' ? '-' : value;

                    return (
                      <Stack key={`mobile-cell-${id}-${column.key}-${index}`} direction="row" spacing={0.75}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96 }}>
                          {column.mobileLabel || column.label}
                        </Typography>
                        <Typography variant="body2">{normalizedValue}</Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

export default ResponsiveDataTable;
